import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { COMPANY, SERVICES, SERVICE_AREAS, PUBLIC_EMAIL } from "@/lib/constants";

/**
 * Global entity schema graph for OBC.
 *
 * Emits ONE canonical RoofingContractor node + WebSite on every OBC page so
 * Google sees a single, consistent North Port, FL business entity site-wide
 * with a single aggregateRating. The @id matches the bootstrap script in
 * index.html so both references normalize to the same entity.
 *
 * Also removes the pre-React bootstrap JSON-LD block (`__brand_jsonld__`)
 * once mounted, to prevent Google from seeing two RoofingContractor nodes
 * for the same entity — that duplication triggers the "Review has multiple
 * aggregate ratings" error in Search Console.
 */
const CANONICAL_ORIGIN = "https://obriencontractingusa.com";
const ORG_ID = `${CANONICAL_ORIGIN}#organization`;

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "RoofingContractor",
  "@id": ORG_ID,
  name: COMPANY.name,
  legalName: COMPANY.legalName,
  telephone: "+1-239-919-4485",
  ...(PUBLIC_EMAIL ? { email: PUBLIC_EMAIL } : {}),
  url: `${CANONICAL_ORIGIN}/`,
  logo: `${CANONICAL_ORIGIN}/images/logo-transparent.png`,
  image: `${CANONICAL_ORIGIN}/images/hero-roofing-1920.webp`,
  description: `${COMPANY.name} is a licensed Florida roofing contractor based in North Port, FL, serving North Port, Venice, Englewood, Port Charlotte, Sarasota, and Bradenton.`,
  foundingDate: "2016",
  priceRange: "$$",
  paymentAccepted: ["Cash", "Credit Card", "Check", "Financing Available"],
  sameAs: [
    COMPANY.social.facebook,
    COMPANY.social.yelp,
    COMPANY.social.instagram,
    COMPANY.social.tiktok,
  ].filter(Boolean),
  address: {
    "@type": "PostalAddress",
    streetAddress: COMPANY.address.street,
    addressLocality: COMPANY.address.city, // North Port
    addressRegion: COMPANY.address.state,  // FL
    postalCode: COMPANY.address.zip,
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 27.0442,
    longitude: -82.2359,
  },
  areaServed: SERVICE_AREAS.map((area) => ({
    "@type": "City",
    name: area.name,
  })),
  serviceType: [
    "roof replacement",
    "roof repair",
    "roof inspection",
    "storm damage inspection",
    "shingle roofing",
    "tile roofing",
    "metal roofing",
    "flat roofing",
    "gutters",
    "soffit",
    "fascia",
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "07:00",
      closes: "18:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Saturday",
      opens: "08:00",
      closes: "14:00",
    },
  ],
  additionalProperty: [
    {
      "@type": "PropertyValue",
      name: "Florida roofing contractor license",
      value: COMPANY.license,
    },
  ],
  founder: { "@type": "Person", name: "Christopher O'Brien" },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Roofing Services",
    itemListElement: SERVICES.map((service) => ({
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: service.name,
        description: service.description,
      },
    })),
  },
  // Single source of truth for aggregate rating on the OBC entity. Kept in
  // sync with COMPANY.googleRating / googleReviews. Do NOT emit another
  // aggregateRating for this entity from anywhere else — Google will flag
  // "Review has multiple aggregate ratings".
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: COMPANY.googleRating.toFixed(1),
    reviewCount: String(COMPANY.googleReviews),
    bestRating: "5",
    worstRating: "1",
  },
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${CANONICAL_ORIGIN}/#website`,
  url: `${CANONICAL_ORIGIN}/`,
  name: COMPANY.name,
  publisher: { "@id": ORG_ID },
};

const JsonLdSchema = () => {
  // Remove the pre-React bootstrap JSON-LD block once React mounts, so only
  // this unified RoofingContractor node remains for Google's rich-result
  // validators. Prevents duplicate aggregateRating on the same entity.
  useEffect(() => {
    const prev = document.getElementById("__brand_jsonld__");
    if (prev) prev.remove();
  }, []);

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
    </Helmet>
  );
};

export default JsonLdSchema;
